import { registryComponent } from '@boost-sd/components-registry/registry';
import { useVDomToString } from '@boost-sd/core-js';
import { getAppHistory } from '@boost-sd/core-js/history';
import type { CTAButtonProps } from '@components/CallToActionButton';
import CTAButton from '@components/CallToActionButton';
import FormatCurrency from '@components/FormatCurrency';
import IntegrationProductRating from '@components/IntegrationProductRating';
import InventoryStatus from '@components/InventoryStatus';
import PopupSelectOption from '@components/PopupSelectOption';
import ProductImage from '@components/ProductImage';
import ProductInfo from '@components/ProductInfo';
import type { ProductItemProps } from '@components/ProductItem';
import { ProductItemGridViewLayout } from '@components/ProductItem';
import ProductLabel from '@components/ProductLabel';
import ProductPrice from '@components/ProductPrice';
import TranslateWithComponent from '@components/ProductPrice/TranslateWithComponent';
import type { ProductSwatchHandler, ProductSwatchProps } from '@components/ProductSwatch';
import ProductSwatch from '@components/ProductSwatch';
import ProductTitle from '@components/ProductTitle';
import ProductVendor from '@components/ProductVendor';
import useGeneralSettings from '@hooks/useGeneralSettings';
import useTranslation from '@hooks/useTranslation';
import type { AddToCartState } from '@providers/CartProvider';
import { useFilterSettings } from '@providers/FilterProvider';
import type { ProductListItem as ProductListItemType } from '@providers/ProductListProvider';
import { useProduct } from '@providers/ProductProvider';
import { useProductItemThemeSettings } from '@providers/ThemeProvider/Provider/ProductItemThemeSettings';
import type { CTAButtonType } from '@providers/ThemeProvider/types';
import { buildProductDetailUrlWithVariant, isMobileWidth, setWindowLocation } from '@utils';
import classnames from '@utils/classnames';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';

import { useConnectProductImg } from './connectors/connectProductImg';
import { useConnectProductInfo } from './connectors/connectProductInfo';

export type ProductListItemProps = {
  product: ProductListItemType;
  onClick?: () => unknown;
  onRender?: (props: ProductItemProps) => React.JSX.Element;
  autoAspectRatio?: `${number}/${number}`;
};

const ProductItem = ({
  product,
  onClick,
  onRender: customRender,
  autoAspectRatio,
}: ProductListItemProps) => {
  const { t } = useTranslation();

  const { showVariantImageBasedOnSelectedFilter, isShortenUrlParam, urlScheme, filterPrefixParam } =
    useFilterSettings();

  const {
    generalSettings: {
      no_image_url,
      current_tags,
      addCollectionToProductUrl: hasCollection,
      priceSettings,
    },
  } = useGeneralSettings();

  const isSaleCustom = priceSettings?.saleCondition?.(product);

  const [addToCartState, setAddToCartState] = useState<AddToCartState>({
    isAdding: false,
    success: false,
    fail: false,
  });

  const [isOpenPopup, setIsOpenPopup] = useState<boolean>(false);

  // Calculate selected variant image from filter first
  const optionName = showVariantImageBasedOnSelectedFilter.replace(`${filterPrefixParam}opt_`, '');
  const isShortenUrl = isShortenUrlParam && urlScheme !== 0;

  const urlParams = new URLSearchParams(getAppHistory().location.search);

  const filterValueToShowVariantImage = urlParams.getAll(
    isShortenUrl ? optionName : showVariantImageBasedOnSelectedFilter
  );

  const selectedVariantImageByFilterOption = useMemo(() => {
    let selectedVariantImageByFilterOption = '';

    // Skip if no filter values or no variants
    if (!filterValueToShowVariantImage.length || !product.variants?.length) {
      return selectedVariantImageByFilterOption;
    }

    filterValueToShowVariantImage.every((value) => {
      // For split products (with variant_id), try to find variant with matching option
      // but be more flexible about matching across different variant structures
      let selectedVariantByFilterOption;

      if (product.variant_id) {
        // For split products, look for any variant that has the specific option
        selectedVariantByFilterOption = product.variants.find((item) => {
          // Check both merged_options and original_merged_options
          const mergedOptions = item.original_merged_options || item.merged_options || [];
          return mergedOptions.some((option) => {
            const optionLower = option.toLowerCase();
            const optionNameLower = optionName.toLowerCase();
            const valueLower = value.toLowerCase();

            // Primary match: exact option:value format
            if (optionLower === `${optionNameLower}:${valueLower}`) {
              return true;
            }

            // Secondary match: option starts with optionName: and contains value
            if (optionLower.startsWith(`${optionNameLower}:`) && optionLower.includes(valueLower)) {
              return true;
            }

            // Fallback: exact value match only (for cases where option format is just the value)
            if (optionLower === valueLower) {
              return true;
            }

            return false;
          });
        });
      } else {
        // For regular products, use the existing logic
        selectedVariantByFilterOption = product.variants.find((item) =>
          item.merged_options?.includes(`${optionName}:${value}`)
        );
      }

      if (selectedVariantByFilterOption?.image) {
        selectedVariantImageByFilterOption = selectedVariantByFilterOption.image;
        return false;
      }
      return true;
    });

    return selectedVariantImageByFilterOption;
  }, [
    filterValueToShowVariantImage.length,
    filterValueToShowVariantImage[0],
    optionName,
    product.variants,
    product.variant_id,
  ]);

  const {
    isSale,
    isSoldOut,
    previewProductImages,
    salePercent,
    saleAmount,
    labelByTag,
    firstImage,
  } = useProduct(product, no_image_url, selectedVariantImageByFilterOption);

  const saleLabelString = useVDomToString({ vDOM: <FormatCurrency value={saleAmount} /> });

  const isProductSale = isSaleCustom !== undefined ? isSaleCustom : isSale;

  const [productImgToShow, setProductImgToShow] = useState<Array<string>>(previewProductImages);

  const {
    productImg: { elements: productImgElementsSettings },
    general: productItemGeneralSettings,
  } = useProductItemThemeSettings();

  const hideOtherLabelsWhenSoldOut =
    productImgElementsSettings?.productSoldOutLabel?.hideOtherLabelsWhenSoldOut;

  const handleClosePopup = () => {
    setIsOpenPopup(false);
  };

  const { props: productInfoProps } = useConnectProductInfo(product);
  const { props: productImgProps } = useConnectProductImg(product);

  const renderProductSwatchesElement = (place: 'img' | 'info') => {
    const elements: Record<`swatches.${number}`, React.ReactElement<ProductSwatchProps>> = {};

    const wrapperSettings = place === 'img' ? productImgProps : productInfoProps;

    const swatchesSettings = wrapperSettings.elements.swatches;

    [1, 2, 3].forEach((key) => {
      const swatchHandler = useRef<ProductSwatchHandler<string> | null>(null);

      if (!swatchesSettings?.[`swatches.${key}`]) return;

      const handleHover = (_: string, image?: string) => {
        if (swatchesSettings[`swatches.${key}`].changeProductImageOn === 'hover' && image)
          setProductImgToShow([image]);
      };

      const handleBlur = () => {
        if (swatchesSettings[`swatches.${key}`].changeProductImageOn === 'hover') {
          setProductImgToShow(previewProductImages);
        }
      };

      const handleSelect = (_: string, image?: string) => {
        if (swatchesSettings[`swatches.${key}`].changeProductImageOn === 'click') {
          image ? setProductImgToShow([image]) : setProductImgToShow(previewProductImages);
        }
      };

      const handleClickOutSide = (selectedValue?: string) => {
        if (selectedValue) {
          swatchHandler.current?.selectOption('');
          setProductImgToShow(previewProductImages);
        }
      };

      elements[`swatches.${key}`] = (
        <ProductSwatch<string>
          key={`product-swatch-${key}`}
          {...swatchesSettings[`swatches.${key}`]}
          maxItem={4}
          onHover={handleHover}
          onBlur={handleBlur}
          onSelect={handleSelect}
          onClickOutSide={handleClickOutSide}
          handler={swatchHandler}
          swatchButtonTextKey={`productItem.swatchButtonText${key}`}
          isFullWidth={place === 'info'}
        />
      );
    });

    return elements;
  };

  const renderCustomLabelByTag = () => {
    const { elements: productImgElementsProps } = productImgProps;

    return labelByTag?.map((item, index) => (
      <ProductLabel
        key={`custom-label-${index}`}
        {...productImgElementsProps.customLabelByTag}
        labelType='customLabelByTag'
        labelText={item}
      />
    ));
  };

  const renderCTAElement = (place: 'img' | 'info') => {
    const elements: {
      qvBtn?: React.ReactElement<CTAButtonProps>;
      selectOptionBtn?: React.ReactElement<CTAButtonProps>;
      goToDetailBtn?: React.ReactElement<CTAButtonProps>;
    } = {};

    const wrapperSettings = place === 'img' ? productImgProps : productInfoProps;

    const ctaSettings = wrapperSettings.elements.cta;

    if (ctaSettings) {
      ['qvBtn', 'selectOptionBtn', 'goToDetailBtn'].forEach((key) => {
        if (ctaSettings[key as CTAButtonType]) {
          const { action, displayType } = ctaSettings[key as CTAButtonType] || {};

          let handleOnClick: () => void = () => false;

          let buttonText: string | undefined = undefined;

          if (key === 'qvBtn') {
            handleOnClick = () => {
              window.dispatchEvent(
                new CustomEvent('boost-sd-open-quick-view', {
                  detail: {
                    productData: product,
                    type: 'full',
                  },
                })
              );
            };

            buttonText = 'productItem.qvBtnLabel';
          }

          if (key === 'selectOptionBtn') {
            if (isOpenPopup) return;

            buttonText = 'productItem.atcSelectOptionsLabel';

            if (product?.variants?.length === 1) {
              buttonText = addToCartState?.isAdding
                ? 'productItem.atcAddingToCartBtnLabel'
                : addToCartState?.success
                ? 'productItem.atcAddedToCartBtnLabel'
                : addToCartState?.fail
                ? 'productItem.atcFailedToCartBtnLabel'
                : 'productItem.atcAvailableLabel';

              handleOnClick = () => {
                if (addToCartState?.isAdding) return;
                setAddToCartState((prev) => ({ ...prev, isAdding: true }));
                window.dispatchEvent(
                  new CustomEvent('boost-sd-add-to-cart', {
                    detail: {
                      variantId: product?.variants?.[0]?.id,
                      quantity: 1,
                      onSuccess: () => {
                        setTimeout(() => {
                          setAddToCartState((prev) => ({
                            fail: false,
                            isAdding: false,
                            success: false,
                          }));
                        }, 350);
                        setAddToCartState((prev) => ({
                          ...prev,
                          isAdding: false,
                          success: true,
                        }));
                      },
                      onFail: () => {
                        setTimeout(() => {
                          setAddToCartState((prev) => ({
                            fail: false,
                            isAdding: false,
                            success: false,
                          }));
                        }, 350);
                        setAddToCartState((prev) => ({ ...prev, fail: true, isAdding: false }));
                      },
                    },
                  })
                );
              };
            } else {
              if (action === 'popup') {
                handleOnClick = () =>
                  window.dispatchEvent(
                    new CustomEvent('boost-sd-open-quick-view', {
                      detail: {
                        productData: product,
                        type: 'mini',
                      },
                    })
                  );
              }

              if (action === 'quickAddToCart') {
                if (isMobileWidth()) {
                  handleOnClick = () =>
                    window.dispatchEvent(
                      new CustomEvent('boost-sd-open-quick-view', {
                        detail: {
                          productData: product,
                          type: 'mini',
                        },
                      })
                    );
                } else {
                  handleOnClick = () => setIsOpenPopup(true);
                }
              }

              if (action === 'productPage') {
                handleOnClick = () =>
                  setWindowLocation(
                    buildProductDetailUrlWithVariant(
                      product,
                      hasCollection,
                      current_tags || undefined
                    )
                  );
              }
            }

            if (isSoldOut) buttonText = 'productItem.soldoutLabel';
          }

          if (key === 'goToDetailBtn') {
            handleOnClick = () =>
              setWindowLocation(
                buildProductDetailUrlWithVariant(product, hasCollection, current_tags || undefined)
              );
            buttonText = 'productItem.viewProductBtnLabel';
          }

          if (displayType === 'icon') buttonText = undefined;

          elements[key as CTAButtonType] = (
            <CTAButton
              disabled={key === 'selectOptionBtn' && isSoldOut}
              key={`${product.id}-${key}`}
              onClick={handleOnClick}
              {...ctaSettings[key as CTAButtonType]}
              text={buttonText}
              className={key === 'qvBtn' ? classnames('btn-quick-view') : ''}
            />
          );
        }
      });
    }

    return elements;
  };

  const inventoryQuantity = useMemo(() => {
    if (!product?.variants?.length) return null;

    if (product?.variant_id) {
      const productVariant = product?.variants?.find((item) => +item.id === product.variant_id);
      return productVariant?.inventory_quantity ?? null;
    }

    return product?.variants?.reduce((total, item) => (total += item.inventory_quantity), 0);
  }, [product]);

  const onRender = () => {
    const { title, vendor, price_min, price_max, compare_at_price_max, compare_at_price_min } =
      product;

    const { elements: productInfoElementsProps } = productInfoProps;
    const { elements: productImgElementsProps } = productImgProps;

    const productItemProps: ProductItemProps = {
      product,
      onClick,
      productInfo: (
        <ProductInfo
          {...productInfoProps}
          elements={{
            title: productInfoElementsProps.title && (
              <ProductTitle {...productInfoElementsProps.title} title={title} />
            ),
            price: productInfoElementsProps.price && (
              <ProductPrice
                setting={productInfoElementsProps.price}
                data={{
                  isSale: isProductSale,
                  salePercent,
                  saleAmount,
                  price_min,
                  price_max,
                  compare_at_price_max,
                  compare_at_price_min,
                }}
              />
            ),
            vendor: productInfoElementsProps.vendor && (
              <ProductVendor {...productInfoElementsProps.vendor} vendor={vendor} />
            ),
            inventoryStatus: productInfoElementsProps.inventoryStatus && (
              <InventoryStatus
                {...productInfoElementsProps.inventoryStatus}
                inventory={inventoryQuantity}
                isSoldOut={isSoldOut}
              />
            ),
            rating: productInfoElementsProps.rating ? (
              <IntegrationProductRating key='rating' textAlign={productInfoProps.textAlign} />
            ) : (
              <></>
            ),
            ...renderProductSwatchesElement('info'),
            ...renderCTAElement('info'),
          }}
        />
      ),
      productImg: (
        <ProductImage
          {...productImgProps}
          productId={product.variant_id || product.id}
          images={productImgToShow}
          altImage={title}
          autoAspectRatio={autoAspectRatio}
          naturalAspectRatio={firstImage && `${firstImage.width}/${firstImage.height}`}
          originalSize={product.images_info}
          elements={{
            rating: productImgElementsProps.rating ? (
              <IntegrationProductRating key='rating' />
            ) : (
              <></>
            ),
            saleLabel:
              isProductSale &&
              productImgElementsProps.saleLabel &&
              !(isSoldOut && hideOtherLabelsWhenSoldOut) ? (
                <ProductLabel
                  key='sale-label'
                  {...productImgElementsProps.saleLabel}
                  labelType='sale'
                  labelText={
                    <TranslateWithComponent
                      translation='productItem.productItemSale'
                      data={{
                        salePercent,
                        saleAmount: saleLabelString,
                      }}
                    />
                  }
                />
              ) : undefined,
            soldOutLabel:
              isSoldOut && productImgElementsProps.soldOutLabel ? (
                <ProductLabel
                  key='sold-out-label'
                  {...productImgElementsProps.soldOutLabel}
                  labelType='soldout'
                  labelText={t('productItem.productItemSoldOut')}
                />
              ) : undefined,
            customLabelByTag:
              productImgElementsProps.customLabelByTag &&
              !(isSoldOut && hideOtherLabelsWhenSoldOut) ? (
                <Fragment key='custom-label'>{renderCustomLabelByTag()}</Fragment>
              ) : undefined,
            ...renderCTAElement('img'),
            ...renderProductSwatchesElement('img'),
          }}
        />
      ),
      popupSelectOption: isOpenPopup ? (
        <PopupSelectOption
          variants={product.variants || []}
          optionsWithValue={product.options_with_values || []}
          handleClosePopupSelectOption={handleClosePopup}
          isSoldOut={isSoldOut}
        />
      ) : (
        <></>
      ),
      ...productItemGeneralSettings,
    };

    if (customRender) return customRender(productItemProps);

    return <ProductItemGridViewLayout {...productItemProps} hasCollection={hasCollection} />;
  };

  useEffect(() => {
    setProductImgToShow(previewProductImages);
  }, [selectedVariantImageByFilterOption, previewProductImages]);

  return onRender();
};

export default registryComponent('ProductItemWidget', ProductItem);
